import React, { MouseEvent } from 'react';
import styled from 'styled-components';

import Divider from '@/components/atom/Divider';
import MaterialIcon from '@/components/atom/MaterialIcon';
import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import DefaultDoctorProfile from '@/components/atom/icon/DefaultDoctorProfile';
import Dot from '@/components/atom/icon/Dot';
import DotMenu from '@/components/atom/icon/DotMenu';
import { LineClampTextStyled, SelectableText } from '@/components/atom/text/DefaultText';
import LazyImage from '@/components/molecules/LazyImage';
import NurseProfile from '@/components/molecules/NurseProfile';
import BlockQuestion from '@/components/molecules/QuestionList/QuestionItem/BlockQuestion';
import AnswerReview from '@/components/templates/mobile/communication/Qna/QnaDetailMobileTemplate/QnaAnswers/AnswerDetail/AnswerReview';
import AnswerWriteReview from '@/components/templates/mobile/communication/Qna/QnaDetailMobileTemplate/QnaAnswers/AnswerDetail/AnswerWriteReview';
import { QuestionDetailModalDataType } from '@/hooks/qna/useQuestionDetailViewModel';
import { REPORT_TYPE, ROLE_TYPE } from '@/hooks/useReportModal';
import theme from '@/styles/theme';
import { AnswerLikeType, AnswererType } from '@/types/bff/gql-schema';
import { Nullable } from '@/types/common';
import { QnaDetailAnswerVo } from '@/types/qna';
import { absoluteTime, getAnswerDetail, getQndAnswererMedicalStaffType } from '@/utils/format';

import AnswerFootnote from './AnswerFootnote';
import AnswererHospital from './AnswererHospital';

export interface AnswerDetailProps {
  answer: QnaDetailAnswerVo;
  onShowModal: (e: MouseEvent<HTMLButtonElement>, data: QuestionDetailModalDataType) => void;
  onClickWriteReview: (answerPid: string) => void;
  onClickLikeAnswer: (answerPid: string, type: AnswerLikeType, myLikeType: Nullable<AnswerLikeType>) => void;
  onClickMagazine: (magazinePid: string) => void;
  moveToHospitalDetail: (hospitalPid: string) => void;
  moveToDoctorDetailPage: (answererType: AnswererType, doctorId?: string | null) => void;
}
const AnswerDetail = ({
  answer,
  onShowModal,
  onClickLikeAnswer,
  onClickMagazine,
  onClickWriteReview,
  moveToHospitalDetail,
  moveToDoctorDetailPage,
}: AnswerDetailProps) => {
  return (
    <>
      {/* 답변한 의사/간호사 */}
      <AnswerWrapper>
        <AnswerTop
          onClick={() => {
            moveToDoctorDetailPage(answer.answerer.answererType, answer.answerer.answererPublicId);
          }}
        >
          <AnswerWriterInfo>
            {answer.answerer.answererType === AnswererType.Nurse ? (
              <NurseProfile url={answer.answerer.profileImageUrl} size={48} />
            ) : (
              <LazyImage
                width={48}
                height={48}
                objectFit={'cover'}
                loader={<DefaultDoctorProfile />}
                src={answer.answerer.profileImageUrl}
              />
            )}
            <AnswerWriter>
              <AnswerWriterName>
                {getQndAnswererMedicalStaffType(answer.answerer.answererType, answer.answerer.name)}
              </AnswerWriterName>
              <AnswerWriterHospital>
                <AnswerWriterHospitalName lineNumber={1} isSelectable>
                  {answer.answerer.affiliatedInstitutionName}
                </AnswerWriterHospitalName>
                <Dot size={3} areaWidth={16} color={theme.color.gray300} />
                <span>{answer.createdDate}</span>
              </AnswerWriterHospital>
            </AnswerWriter>
            <Time dateTime={absoluteTime(answer.createdAt)} />
          </AnswerWriterInfo>
          <ReportButton
            type='button'
            onClick={(e) =>
              onShowModal(e, {
                report: {
                  targetId: answer.answerPid,
                  targetType: REPORT_TYPE.ANSWER,
                  roleId: answer.answerer.answererId,
                  role: answer.answerer.answererType === ROLE_TYPE.AI ? ROLE_TYPE.AI : ROLE_TYPE.DOCTOR,
                  isBlocked: answer.isBlockedByMe,
                },
              })
            }
          >
            <DotMenu />
          </ReportButton>
        </AnswerTop>

        {/* 답변 (차단/미차단) */}
        {answer.isBlockedByMe ? (
          <BlockQuestion />
        ) : (
          <AnswerContentWrapper>
            <AnswerContent>{getAnswerDetail(answer)}</AnswerContent>

            {answer.recommendedMagazineGraph && (
              <RecommendMagazine>
                <h3>선생님이 추천한 콘텐츠예요</h3>
                <RecommendMagazineWrapper
                  role='button'
                  onClick={() => onClickMagazine(answer.recommendedMagazineGraph?.magazinePid ?? '')}
                >
                  <RecommendMagazineContent>
                    <h4>{answer.recommendedMagazineGraph.title}</h4>
                    <p>{answer.recommendedMagazineGraph.description}</p>
                  </RecommendMagazineContent>
                  <RecommendMagazineImage src={answer.recommendedMagazineGraph.titleImageUrl} />
                </RecommendMagazineWrapper>
              </RecommendMagazine>
            )}

            {/* 답변 작성 의사 병원 정보 */}
            {answer.answerer.answererType === AnswererType.Doctor && !!answer.answerer.affiliation && (
              <AnswererHospital affiliation={answer.answerer.affiliation} moveToHospitalDetail={moveToHospitalDetail} />
            )}

            {/* Footnote */}
            <AnswerFootnote answer={answer} />

            {/* NPS (만족해요/아쉬워요) */}
            {answer.isReviewViewable ? (
              answer.hasReview ? (
                <AnswerReviewListWrapper>
                  {answer.reviews.map((review) => (
                    <AnswerReview key={`answer-review-${review.id}`} review={review} />
                  ))}
                </AnswerReviewListWrapper>
              ) : (
                <AnswerWriteReview
                  title={'닥터나우 의료진 답변 어떠셨나요?'}
                  description={'목소리를 듣고 좋은 서비스를 제공해드릴게요.'}
                  onClickWriteReview={() => {
                    onClickWriteReview(answer.answerPid);
                  }}
                />
              )
            ) : (
              <AnswerNps>
                <NpsButton
                  isChecked={answer.myLikeType === AnswerLikeType.Like}
                  onClick={() => onClickLikeAnswer(answer.answerPid, AnswerLikeType.Like, answer.myLikeType)}
                >
                  <MaterialIcon
                    name={'thumb_up'}
                    size={16}
                    filled={answer.myLikeType === AnswerLikeType.Like}
                    color={answer.myLikeType === AnswerLikeType.Like ? theme.color.primary600 : theme.color.gray600}
                  />
                  만족해요
                  <LikeCount $display={!!answer.likeCount}>{answer.likeCount}</LikeCount>
                </NpsButton>

                <NpsButton
                  isChecked={answer.myLikeType === AnswerLikeType.Dislike}
                  onClick={() => onClickLikeAnswer(answer.answerPid, AnswerLikeType.Dislike, answer.myLikeType)}
                >
                  <MaterialIcon
                    name={'thumb_down'}
                    size={16}
                    filled={answer.myLikeType === AnswerLikeType.Dislike}
                    color={answer.myLikeType === AnswerLikeType.Dislike ? theme.color.primary600 : theme.color.gray600}
                  />
                  아쉬워요
                  <LikeCount $display={!!answer.dislikeCount}>{answer.dislikeCount}</LikeCount>
                </NpsButton>
              </AnswerNps>
            )}
          </AnswerContentWrapper>
        )}
      </AnswerWrapper>
      <Divider height={'15px'} color={theme.color.gray30} />
    </>
  );
};

export default AnswerDetail;

const AnswerWrapper = styled.li`
  background: ${({ theme }) => theme.color.white};

  cursor: default;
  &:hover {
    cursor: default;
  }
`;

const AnswerTop = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 24px;
  align-items: center;

  ${({ theme }) => `
    border-bottom: 1px solid ${theme.color.gray30};
  `};
`;

const AnswerWriterInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const AnswerWriter = styled.div`
  display: flex;
  flex-flow: column;
  margin-left: 16px;
  align-items: flex-start;
`;

const AnswerWriterName = styled.div`
  ${({ theme }) => `
    ${theme.typography.UI16SB};
    color: ${theme.color.gray700};
  `};
`;

const AnswerWriterHospital = styled.div`
  display: flex;
  flex: 1 1;
  align-items: center;

  ${({ theme }) => `
    color: ${theme.color.gray500};
    span {
      ${theme.typography.UI14M};
    }
  `};
`;

const AnswerWriterHospitalName = styled(LineClampTextStyled)`
  flex: 1;
  ${({ theme }) => `
    ${theme.typography.UI14M};
    `};
`;

const Time = styled.time`
  visibility: hidden;
`;

const ReportButton = styled(DefaultButtonStyled)`
  width: 32px;
  height: 32px;
  border-radius: 4px;

  &:active {
    background: ${({ theme }) => theme.color.gray50};
  }
`;

const AnswerContentWrapper = styled.div`
  padding: 24px;
  ${({ theme }) => `
    border-bottom: 1px solid ${theme.color.gray50};
  `};
`;

const AnswerContent = styled(SelectableText)`
  white-space: pre-wrap;
  ${({ theme }) => `
    ${theme.typography.ARTICLE16R};
    color: ${theme.color.gray500};
    `}
`;

const AnswerNps = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;
`;

const NpsButton = styled(DefaultButtonStyled)<{ isChecked: boolean }>`
  padding: 10px 0;
  box-sizing: border-box;
  font-weight: 600;
  font-size: 14px;
  vertical-align: middle;
  border-radius: 5px;
  ${({ isChecked, theme }) => `
    ${theme.typography.UI14SB};
    color: ${isChecked ? theme.color.primary500 : theme.color.gray600};
    border: 1px solid
      ${isChecked ? theme.color.primary500 : theme.color.gray200};
      `};

  gap: 8px;
`;

const LikeCount = styled.span<{ $display: boolean }>`
  ${({ theme, $display }) => `
      display: ${$display ? 'block' : 'none'};
      ${theme.typography.UI14SB};
      `};
`;

const RecommendMagazine = styled.div`
  margin-top: 24px;
  width: 100%;
  display: flex;
  flex-direction: column;

  h3 {
    ${({ theme }) => theme.typography.UI16SB};
    color: ${({ theme }) => theme.color.gray700};
    margin-bottom: 12px;
  }
`;

const RecommendMagazineWrapper = styled.section`
  display: flex;
  border: 1px solid ${({ theme }) => theme.color.gray100};
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  width: 100%;
  max-height: 130px;
`;

const RecommendMagazineContent = styled.div`
  padding: 18px 16px;
  flex: 1;

  & > h4 {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    margin: 0 0 4px 0;
    width: 100%;
    ${({ theme }) => `
      ${theme.typography.UI14SB};
      color: ${theme.color.primary500};
      `};
  }

  & > p {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    ${({ theme }) => `
      ${theme.typography.UI12M}
      color: ${theme.color.gray500};
      `};
  }
`;

const RecommendMagazineImage = styled.img`
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const AnswerReviewListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
